import React from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ArrowDownToLine, ArrowUpToLine, FoldVertical, TextAlignCenter, TextAlignEnd, TextAlignStart } from "lucide-react";
import { DropdownColumnSubMenu, NumberColumnSubMenu } from "./ColumnSubMenus";
import {
  FIELD_PROPERTIES_LABELS,
  FIELD_PROPERTIES_TABLE_COLUMN_COUNT_ITEMS,
  FIELD_PROPERTIES_TABLE_COLUMN_TYPE_ITEMS,
  FORM_ELEMENT_TYPES,
  NO_SELECTION_TEXT,
  TABLE_FIELD_DEFAULT_COLUMN_COUNT,
  TEXT_LABEL_DEFAULT_FONT_SIZE,
  TEXT_LABEL_FONT_SIZE_OPTIONS,
} from "./FormsConstant";
import "./styles/FieldProperties.scss";

/**
 * Builds the `columns` array when the table column count changes.
 * @param {Array<Object>} currentColumns
 * @param {number} newCount
 * @returns {Array<Object>}
 * @author Abhishek Dhillon
 */
const buildColumnsForTableCount = (currentColumns, newCount) => {
  const cols = currentColumns || [];
  if (newCount > cols.length) {
    const startLen = cols.length;
    return [
      ...cols,
      ...Array.from({ length: newCount - startLen }, (_, i) => {
        const ord = startLen + i + 1;
        return {
          id: startLen + i,
          field: `column${ord}`,
          title: `${FIELD_PROPERTIES_LABELS.COLUMN_TITLE_PREFIX}${ord}`,
          type: "text",
        };
      }),
    ];
  }
  return cols.slice(0, newCount);
};

/**
 * Returns a new columns array after changing one column's type (dropdown gets default options).
 * @param {Array<Object>} columns
 * @param {number} columnIndex
 * @param {string} newType
 * @returns {Array<Object>}
 * @author Abhishek Dhillon
 */
const mapColumnsAfterTypeChange = (columns, columnIndex, newType) => {
  const next = [...columns];
  const base = { ...next[columnIndex], type: newType };
  if (newType === FORM_ELEMENT_TYPES.DROPDOWN && !base.options) {
    const t = Date.now();
    base.options = [
      { id: t, value: "" },
      { id: t + 1, value: "" },
    ];
  }
  next[columnIndex] = base;
  return next;
};

/**
 * Small toolbar control used by text-label field properties (FieldProperties).
 * @param {Object} props
 * @param {string} props.buttonId
 * @param {string} props.title
 * @param {boolean} props.active
 * @param {Function} props.onClick
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 */
const FieldPropToolbarButton = ({ buttonId, title, active, onClick, children }) => (
  <button type='button' id={buttonId} className={`toolbar-btn ${active ? "active" : ""}`} onClick={onClick} title={title}>
    {children}
  </button>
);

const TEXT_STYLE_TOGGLES = [
  { key: "bold", buttonId: "field-prop-textlabel-style-bold", title: FIELD_PROPERTIES_LABELS.BOLD, render: () => <strong>B</strong> },
  { key: "italic", buttonId: "field-prop-textlabel-style-italic", title: FIELD_PROPERTIES_LABELS.ITALIC, render: () => <em>I</em> },
  { key: "underline", buttonId: "field-prop-textlabel-style-underline", title: FIELD_PROPERTIES_LABELS.UNDERLINE, render: () => <u>U</u> },
];

const HORIZONTAL_ALIGN_CONTROLS = [
  {
    value: "left",
    buttonId: "field-prop-textlabel-halign-left",
    title: FIELD_PROPERTIES_LABELS.ALIGN_LEFT,
    Icon: TextAlignStart,
  },
  {
    value: "center",
    buttonId: "field-prop-textlabel-halign-center",
    title: FIELD_PROPERTIES_LABELS.ALIGN_CENTER,
    Icon: TextAlignCenter,
  },
  {
    value: "right",
    buttonId: "field-prop-textlabel-halign-right",
    title: FIELD_PROPERTIES_LABELS.ALIGN_RIGHT,
    Icon: TextAlignEnd,
  },
];

const VERTICAL_ALIGN_CONTROLS = [
  {
    value: "top",
    buttonId: "field-prop-textlabel-valign-top",
    title: FIELD_PROPERTIES_LABELS.VALIGN_TOP,
    Icon: ArrowUpToLine,
  },
  {
    value: "middle",
    buttonId: "field-prop-textlabel-valign-middle",
    title: FIELD_PROPERTIES_LABELS.VALIGN_MIDDLE,
    Icon: FoldVertical,
  },
  {
    value: "bottom",
    buttonId: "field-prop-textlabel-valign-bottom",
    title: FIELD_PROPERTIES_LABELS.VALIGN_BOTTOM,
    Icon: ArrowDownToLine,
  },
];

/**
 * Font, style, and alignment editors for a text-label element (FieldProperties).
 * @param {Object} props
 * @param {Object} props.properties
 * @param {Function} props.onPatch - (partial) => void
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 */
const TextLabelFieldPropertiesPanel = ({ properties, onPatch }) => (
  <>
    <div className='property-group'>
      <label>{FIELD_PROPERTIES_LABELS.SIZE}</label>
      <select
        id='field-prop-textlabel-font-size'
        value={properties.fontSize ?? TEXT_LABEL_DEFAULT_FONT_SIZE}
        onChange={(e) => onPatch({ fontSize: parseInt(e.target.value, 10) })}>
        {TEXT_LABEL_FONT_SIZE_OPTIONS.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>

    <div className='property-group'>
      <label>{FIELD_PROPERTIES_LABELS.STYLE}</label>
      <div className='editor-toolbar'>
        {TEXT_STYLE_TOGGLES.map(({ key, buttonId, title: toggleTitle, render: RenderContent }) => (
          <FieldPropToolbarButton
            key={key}
            buttonId={buttonId}
            title={toggleTitle}
            active={Boolean(properties[key])}
            onClick={() => onPatch({ [key]: !properties[key] })}>
            <RenderContent />
          </FieldPropToolbarButton>
        ))}
      </div>
    </div>

    <div className='property-group'>
      <label>{FIELD_PROPERTIES_LABELS.HORIZONTAL_ALIGN}</label>
      <div className='editor-toolbar'>
        {HORIZONTAL_ALIGN_CONTROLS.map(({ value, buttonId, title, Icon }) => (
          <FieldPropToolbarButton
            key={value}
            buttonId={buttonId}
            title={title}
            active={properties.textAlign === value}
            onClick={() => onPatch({ textAlign: value })}>
            <Icon className='lucid-icon-common' />
          </FieldPropToolbarButton>
        ))}
      </div>
    </div>

    <div className='property-group'>
      <label>{FIELD_PROPERTIES_LABELS.VERTICAL_ALIGN}</label>
      <div className='editor-toolbar'>
        {VERTICAL_ALIGN_CONTROLS.map(({ value, buttonId, title, Icon }) => {
          const active = value === "top" ? (properties.verticalAlign || "top") === "top" : properties.verticalAlign === value;
          return (
            <FieldPropToolbarButton key={value} buttonId={buttonId} title={title} active={active} onClick={() => onPatch({ verticalAlign: value })}>
              <Icon className='lucid-icon-common' />
            </FieldPropToolbarButton>
          );
        })}
      </div>
    </div>
  </>
);

/**
 * Table column count and per-column type editors (FieldProperties).
 * @param {Object} props
 * @param {string} props.elementId
 * @param {Object} props.properties
 * @param {Function} props.onPatch - (partial) => void
 * @param {Function} props.onColumnChange
 * @param {Array} [props.usedWorkflowVariableIds]
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 */
const TableFieldPropertiesPanel = ({ elementId, properties, onPatch, onColumnChange, usedWorkflowVariableIds }) => {
  const columnCount = properties.columnCount ?? TABLE_FIELD_DEFAULT_COLUMN_COUNT;
  const columns = properties.columns || [];
  const countValue =
    FIELD_PROPERTIES_TABLE_COLUMN_COUNT_ITEMS.find((o) => o.value === columnCount) ??
    FIELD_PROPERTIES_TABLE_COLUMN_COUNT_ITEMS.find((o) => o.value === TABLE_FIELD_DEFAULT_COLUMN_COUNT);

  /**
   * Handles the change event for the column count dropdown.
   * @param {Object} e - The change event object.
   * @returns {void}
   * @author Abhishek Dhillon
   */
  const handleColumnCountChange = (e) => {
    const newCount = e.target.value?.value;
    if (newCount == null) return;
    onPatch({
      columnCount: newCount,
      columns: buildColumnsForTableCount(properties.columns, newCount),
    });
  };

  /**
   * Handles the change event for the column type dropdown.
   * @param {Object} e - The change event object.
   * @param {number} columnIndex - The index of the column to change.
   * @returns {void}
   * @author Abhishek Dhillon
   */
  const handleColumnTypeChange = (e, columnIndex) => {
    const newType = e.target.value?.value;
    if (newType == null || !properties.columns) return;
    onPatch({ columns: mapColumnsAfterTypeChange(properties.columns, columnIndex, newType) });
  };

  return (
    <>
      <div className='property-group'>
        <label>{FIELD_PROPERTIES_LABELS.TABLE_COLUMNS}</label>
        <DropDownList
          id='field-prop-table-column-count'
          data={FIELD_PROPERTIES_TABLE_COLUMN_COUNT_ITEMS}
          textField='text'
          dataItemKey='value'
          value={countValue}
          onChange={handleColumnCountChange}
        />
      </div>

      {columns.length > 0 && (
        <div className='property-group'>
          <label>{FIELD_PROPERTIES_LABELS.COLUMN_TYPES}</label>
          {columns.map((column, i) => (
            <div key={column.id ?? i} className={`column-type-row border rounded p-2 ${i === columns.length - 1 ? "mb-0" : "mb-3"}`}>
              <div className='d-flex align-items-center mb-2'>
                <span className='column-label'>{column.title || `${FIELD_PROPERTIES_LABELS.COLUMN_TITLE_PREFIX}${i + 1}`}:</span>
                <DropDownList
                  id={`field-prop-table-column-type-${i}`}
                  data={FIELD_PROPERTIES_TABLE_COLUMN_TYPE_ITEMS}
                  textField='text'
                  dataItemKey='value'
                  value={FIELD_PROPERTIES_TABLE_COLUMN_TYPE_ITEMS.find((o) => o.value === (column.type || "text"))}
                  onChange={(e) => handleColumnTypeChange(e, i)}
                  className='m-0'
                />
              </div>

              {column.type === "number" && (
                <NumberColumnSubMenu
                  column={column}
                  columnIndex={i}
                  onColumnChange={onColumnChange}
                  usedWorkflowVariableIds={usedWorkflowVariableIds}
                  elementId={elementId}
                />
              )}

              {column.type === "dropdown" && <DropdownColumnSubMenu column={column} columnIndex={i} onColumnChange={onColumnChange} />}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/**
 * FieldProperties
 * Renders property editors based on selected element type
 * @param {Object} props
 * @param {Object} props.element
 * @param {Function} props.onChange
 * @param {Array} props.usedWorkflowVariableIds
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 */
const FieldProperties = ({ element, onChange, usedWorkflowVariableIds }) => {
  if (!element) {
    return (
      <div className='no-selection'>
        <span>{NO_SELECTION_TEXT}</span>
      </div>
    );
  }

  const { id, type, properties = {} } = element;

  const patchElement = (partial) => {
    onChange?.(id, partial);
  };

  const handleColumnChange = (columnIndex, updatedColumn) => {
    const updatedColumns = [...(properties.columns || [])];
    updatedColumns[columnIndex] = updatedColumn;
    patchElement({ columns: updatedColumns });
  };

  const renderBody = () => {
    if (type === FORM_ELEMENT_TYPES.TEXT_LABEL) {
      return <TextLabelFieldPropertiesPanel properties={properties} onPatch={patchElement} />;
    }
    if (type === FORM_ELEMENT_TYPES.TABLE) {
      return (
        <TableFieldPropertiesPanel
          elementId={id}
          properties={properties}
          onPatch={patchElement}
          onColumnChange={handleColumnChange}
          usedWorkflowVariableIds={usedWorkflowVariableIds}
        />
      );
    }
    return null;
  };

  return <div className={`properties-container ${type === FORM_ELEMENT_TYPES.TEXT_LABEL ? "p-2" : ""}`}>{renderBody()}</div>;
};

export default FieldProperties;
